/**
 * Currency calculator — the horizontal converter plus a multi-currency
 * comparison table.
 *
 * One component serves both surfaces; `surface` only picks the API
 * prefix, since `/api/customer/exchange/*` and `/api/exchange/*` return
 * identical bodies.
 *
 * The conversion request is always made at `amount=1`. Rates are pure
 * multipliers, so the component scales them locally as the user types —
 * no request per keystroke, no debounce, no loading flicker on a number
 * input.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ArrowLeftRight, Check, ChevronsUpDown, Info, RefreshCw, TrendingUp } from "lucide-react";

import { apiRequest } from "@/lib/queryClient";
import { useOrgSettings } from "@/lib/org-settings-context";
import {
  countryFlag,
  defaultComparisonCodes,
  formatMoney,
  formatRate,
  FALLBACK_CURRENCIES,
  type Currency,
} from "@/lib/currencies";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Mirrors the types in `server/services/exchange-rates.ts`.
interface CurrencyCatalog {
  currencies: Currency[];
  disclaimer: string;
}

interface ComparisonRow {
  currency: Currency;
  rate: number;
  inverseRate: number;
  converted: number;
}

interface ConversionResult {
  from: Currency;
  to: Currency;
  amount: number;
  converted: number;
  rate: number;
  inverseRate: number;
  updatedAt: string;
  fetchedAt: string;
  stale: boolean;
  provider: string;
  disclaimer: string;
  comparison: ComparisonRow[];
}

interface CurrencyConverterProps {
  /** Which authenticated API surface to call. */
  surface?: "portal" | "admin";
  /**
   * Layout density.
   *
   * Tailwind breakpoints measure the *viewport*, not the container, so
   * they can't tell a 360px sidebar from a 1400px admin page at the same
   * window size. The caller knows which it is, so it says.
   *
   * - `wide` — one horizontal row: amount, from, swap, to, result.
   * - `compact` — horizontal control row with the result called out
   *   underneath, for columns roughly 500–800px.
   * - `stacked` — everything in a single column, for a narrow side rail.
   *
   * Defaults to `wide` for admin and `stacked` for the portal rail.
   */
  variant?: "wide" | "compact" | "stacked";
  className?: string;
}

export function CurrencyConverter({
  surface = "portal",
  variant = surface === "admin" ? "wide" : "stacked",
  className,
}: CurrencyConverterProps) {
  // Anything that isn't the full-width row shows the result on its own
  // line, and only the widest layout has room for the inverse column.
  const showsResultBand = variant !== "wide";
  const showsInverseColumn = variant === "wide";
  const narrowControls = variant !== "wide";
  const prefix = surface === "admin" ? "/api/exchange" : "/api/customer/exchange";
  const { baseCountry, baseCurrency } = useOrgSettings();

  const [amountInput, setAmountInput] = useState("100");
  // `null` means "follow the org default" — the user hasn't chosen yet.
  const [fromOverride, setFromOverride] = useState<string | null>(null);
  const [toOverride, setToOverride] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const from = fromOverride ?? baseCurrency;
  // India is the platform's primary corridor, so it's the default
  // counterpart unless it's already the source.
  const to = toOverride ?? (from === "INR" ? "USD" : "INR");

  const { data: catalog } = useQuery<CurrencyCatalog>({
    queryKey: [prefix, "currencies"],
    queryFn: async () => {
      const res = await apiRequest("GET", `${prefix}/currencies`);
      return res.json();
    },
    staleTime: Infinity,
  });

  const currencies = catalog?.currencies ?? FALLBACK_CURRENCIES;

  const {
    data: conversion,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery<ConversionResult>({
    queryKey: [prefix, "convert", from, to],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `${prefix}/convert?from=${from}&to=${to}&amount=1`,
      );
      return res.json();
    },
    // The feed refreshes at most hourly; a half-hour client TTL keeps a
    // long-lived dashboard tab from drifting without polling it.
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const amount = useMemo(() => {
    const parsed = Number(amountInput.replace(/,/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }, [amountInput]);

  const swap = () => {
    setFromOverride(to);
    setToOverride(from);
  };

  const defaultCodes = useMemo(
    () => new Set(defaultComparisonCodes(currencies, baseCountry, from)),
    [currencies, baseCountry, from],
  );

  const rows = useMemo(() => {
    const all = conversion?.comparison ?? [];
    const visible = showAll ? all : all.filter((r) => defaultCodes.has(r.currency.code));
    // Selected target first, then platform currencies, then the rest —
    // the user's own pair should never require scrolling to find.
    return [...visible].sort((a, b) => {
      if (a.currency.code === to) return -1;
      if (b.currency.code === to) return 1;
      if (a.currency.platformCurrency !== b.currency.platformCurrency) {
        return a.currency.platformCurrency ? -1 : 1;
      }
      return a.currency.code.localeCompare(b.currency.code);
    });
  }, [conversion, showAll, defaultCodes, to]);

  const fromCurrency = currencies.find((c) => c.code === from);
  const toCurrency = currencies.find((c) => c.code === to);
  const converted = conversion ? amount * conversion.rate : 0;
  const hiddenCount = (conversion?.comparison.length ?? 0) - rows.length;

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-4 space-y-4">
        {/* Header — title, freshness, source */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <TrendingUp className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold leading-tight">Currency Converter</h3>
              <p className="text-xs text-muted-foreground">
                Compare rates across currencies
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <FreshnessBadge conversion={conversion} isFetching={isFetching} />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh rates"
              data-testid="button-refresh-rates"
            >
              <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Horizontal tool — amount | from | swap | to [| result] */}
        <div
          className={cn(
            "grid gap-3 items-end",
            variant === "wide" &&
              "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_auto_minmax(0,1.1fr)_minmax(0,1.2fr)]",
            variant === "compact" &&
              "sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_auto_minmax(0,1fr)]",
            // `stacked` stays one column at every width — the rail is
            // narrow even when the viewport is not.
          )}
        >
          <div className="space-y-1.5">
            <Label htmlFor="fx-amount" className="text-xs text-muted-foreground">
              Amount
            </Label>
            <Input
              id="fx-amount"
              inputMode="decimal"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              className="font-medium"
              data-testid="input-fx-amount"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">From</Label>
            <CurrencyPicker
              value={from}
              currencies={currencies}
              onChange={setFromOverride}
              compact={narrowControls}
              testId="select-fx-from"
            />
          </div>

          <div className="flex justify-center pb-1">
            <Button
              variant="outline"
              size="icon"
              onClick={swap}
              aria-label="Swap currencies"
              className="h-9 w-9 shrink-0"
              data-testid="button-fx-swap"
            >
              <ArrowLeftRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">To</Label>
            <CurrencyPicker
              value={to}
              currencies={currencies}
              onChange={setToOverride}
              compact={narrowControls}
              testId="select-fx-to"
            />
          </div>

          {!showsResultBand && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Converted</Label>
              <div
                className="flex h-9 items-center rounded-md border bg-muted/40 px-3 text-base font-semibold tabular-nums"
                data-testid="text-fx-result"
              >
                {isLoading ? (
                  <Skeleton className="h-4 w-24" />
                ) : isError ? (
                  <span className="text-sm font-normal text-muted-foreground">
                    Unavailable
                  </span>
                ) : (
                  <span className="truncate">
                    {formatMoney(converted, to, toCurrency?.decimals ?? 2)}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Compact layouts give the result its own line so the number
            stays readable at narrow column widths. */}
        {showsResultBand && (
          <div className="rounded-lg border bg-muted/40 p-3">
            <p className="text-xs text-muted-foreground">
              {formatMoney(amount, from, fromCurrency?.decimals ?? 2)} equals
            </p>
            <div className="text-2xl font-bold tabular-nums" data-testid="text-fx-result">
              {isLoading ? (
                <Skeleton className="mt-1 h-7 w-40" />
              ) : isError ? (
                <span className="text-base font-normal text-muted-foreground">
                  Unavailable
                </span>
              ) : (
                formatMoney(converted, to, toCurrency?.decimals ?? 2)
              )}
            </div>
          </div>
        )}

        {/* The pair, both ways round */}
        {conversion && !isError && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="font-medium">
              1 {from} = {formatRate(conversion.rate)} {to}
            </span>
            <span className="text-muted-foreground">
              1 {to} = {formatRate(conversion.inverseRate)} {from}
            </span>
          </div>
        )}

        {isError && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            Exchange rates are temporarily unavailable. Card and wallet
            operations are unaffected.
          </div>
        )}

        {/* Comparison table */}
        {!isError && (
          <div className="rounded-md border">
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">1 {from} =</TableHead>
                    {showsInverseColumn && (
                      <TableHead className="text-right">Inverse</TableHead>
                    )}
                    <TableHead className="text-right">
                      {formatMoney(amount, from, fromCurrency?.decimals ?? 2)} buys
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={4}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        </TableRow>
                      ))
                    : rows.map((row) => (
                        <TableRow
                          key={row.currency.code}
                          className={cn(row.currency.code === to && "bg-primary/5")}
                          data-testid={`row-fx-${row.currency.code}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-lg leading-none" aria-hidden="true">
                                {countryFlag(row.currency.countryCode)}
                              </span>
                              <div className="min-w-0">
                                <div className="font-medium leading-tight">
                                  {row.currency.code}
                                </div>
                                <div className="truncate text-xs text-muted-foreground">
                                  {row.currency.name}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatRate(row.rate)}
                          </TableCell>
                          {showsInverseColumn && (
                            <TableCell className="text-right tabular-nums text-muted-foreground">
                              {formatRate(row.inverseRate)}
                            </TableCell>
                          )}
                          <TableCell className="text-right font-medium tabular-nums">
                            {formatMoney(
                              amount * row.rate,
                              row.currency.code,
                              row.currency.decimals,
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </div>

            {hiddenCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full rounded-none border-t"
                onClick={() => setShowAll(true)}
                data-testid="button-fx-show-all"
              >
                Show {hiddenCount} more {hiddenCount === 1 ? "currency" : "currencies"}
              </Button>
            )}
            {showAll && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full rounded-none border-t"
                onClick={() => setShowAll(false)}
              >
                Show fewer
              </Button>
            )}
          </div>
        )}

        {/* Disclaimer — required alongside any quoted number */}
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            {conversion?.disclaimer ??
              "Indicative mid-market rates for comparison only. Actual conversion rates are set at settlement and may include issuer margins and fees."}
          </span>
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Freshness indicator. Three states the user can actually act on:
 * refreshing, live, or delayed-because-the-feed-is-unreachable.
 */
function FreshnessBadge({
  conversion,
  isFetching,
}: {
  conversion: ConversionResult | undefined;
  isFetching: boolean;
}) {
  if (isFetching) {
    return (
      <Badge variant="secondary" className="gap-1.5 font-normal">
        Updating…
      </Badge>
    );
  }
  if (!conversion) return null;

  const updated = new Date(conversion.updatedAt);
  const relative = Number.isNaN(updated.getTime())
    ? "unknown"
    : formatDistanceToNow(updated, { addSuffix: true });

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant={conversion.stale ? "outline" : "secondary"}
            className={cn(
              "gap-1.5 font-normal cursor-default",
              conversion.stale && "border-amber-500/50 text-amber-600 dark:text-amber-500",
            )}
            data-testid="badge-fx-freshness"
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                conversion.stale ? "bg-amber-500" : "animate-pulse bg-emerald-500",
              )}
              aria-hidden="true"
            />
            {conversion.stale ? "Delayed" : "Live"}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p>Feed updated {relative}.</p>
          {conversion.stale && (
            <p className="mt-1">
              The rate feed is currently unreachable — these are the last
              known rates.
            </p>
          )}
          <p className="mt-1 text-muted-foreground">Source: {conversion.provider}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Searchable currency dropdown with flags.
 *
 * A plain `Select` would work at 20 entries, but the combobox lets
 * people type "rupee" or "IN" instead of hunting the list — and it
 * stays usable if the catalog grows.
 */
function CurrencyPicker({
  value,
  currencies,
  onChange,
  compact = false,
  testId,
}: {
  value: string;
  currencies: Currency[];
  onChange: (code: string) => void;
  /** Drop the currency name from the trigger when the column is narrow. */
  compact?: boolean;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = currencies.find((c) => c.code === value);

  const platform = currencies.filter((c) => c.platformCurrency);
  const others = currencies.filter((c) => !c.platformCurrency);

  const renderItem = (currency: Currency) => (
    <CommandItem
      key={currency.code}
      // cmdk matches on `value`, so fold every searchable field into it.
      value={`${currency.code} ${currency.name} ${currency.countryName} ${currency.countryCode}`}
      onSelect={() => {
        onChange(currency.code);
        setOpen(false);
      }}
      className="gap-2"
    >
      <span className="text-lg leading-none" aria-hidden="true">
        {countryFlag(currency.countryCode)}
      </span>
      <span className="font-medium">{currency.code}</span>
      <span className="truncate text-muted-foreground">{currency.name}</span>
      {currency.code === value && <Check className="ml-auto h-4 w-4 shrink-0" />}
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="text-lg leading-none" aria-hidden="true">
              {countryFlag(selected?.countryCode ?? "")}
            </span>
            <span className="font-medium">{selected?.code ?? value}</span>
            {!compact && (
              <span className="hidden truncate text-muted-foreground lg:inline">
                {selected?.name}
              </span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search currency or country…" />
          <CommandList>
            <CommandEmpty>No currency found.</CommandEmpty>
            <CommandGroup heading="Platform currencies">
              {platform.map(renderItem)}
            </CommandGroup>
            {others.length > 0 && (
              <CommandGroup heading="Other currencies">
                {others.map(renderItem)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
